import { useSyncExternalStore, type ReactNode } from 'react';
import { Menu, Tooltip, UnstyledButton } from '@mantine/core';
import { LANGS, setLang, useI18n } from '../../i18n';
import { IconCheck, IconMenu, IconMoon, IconSun } from '../../icons';
import { getSettings, setTheme, subscribeSettings } from '../../settings';
import { useAppState } from '../../state';
import { useNav } from './NavContext';
import { ZoomControl } from './ZoomControl';
import classes from './layout.module.css';

interface Props {
  title: string;
  /** Число рядом с заголовком (например, сколько запросов в списке). */
  count?: number;
  subtitle?: string;
  /** Кнопки и поиск справа. */
  actions?: ReactNode;
  /** Вкладки режимов под заголовком. */
  tabs?: ReactNode;
  /** Показать масштаб таблиц (на экранах с таблицами). */
  zoom?: boolean;
}

/** Шапка экрана: заголовок, действия справа, масштаб таблиц, язык, тема и вкладки режимов. */
export function PageHeader({ title, count, subtitle, actions, tabs, zoom }: Props) {
  const { lang, t } = useI18n();
  const { openNav } = useNav();
  const { state } = useAppState();
  const dark = useSyncExternalStore(subscribeSettings, getSettings).theme === 'dark';
  const current = LANGS.find((l) => l.id === lang)!;

  return (
    <div className={classes.pageHead}>
      <div className={classes.topBar}>
        <UnstyledButton className={classes.burger} onClick={openNav} aria-label={t('Меню')}>
          <IconMenu size={18} />
        </UnstyledButton>
        <div className={classes.titleCol}>
          <div className={classes.titleBlock}>
            <h1 className={classes.pageTitle}>{title}</h1>
            {count !== undefined && <span className={classes.titleCount}>{count}</span>}
            {state?.demo && <span className={classes.demo}>{t('Демо-данные')}</span>}
          </div>
          {subtitle && <span className={classes.pageSub}>{subtitle}</span>}
        </div>
        <div className={classes.topActions}>
          {actions}
          {zoom && <ZoomControl />}
          <Menu position="bottom-end" width={170}>
            <Menu.Target>
              <Tooltip label={t('Язык интерфейса')}>
                <UnstyledButton className={classes.langBtn} aria-label={t('Язык интерфейса')}>
                  {current.short}
                </UnstyledButton>
              </Tooltip>
            </Menu.Target>
            <Menu.Dropdown>
              {LANGS.map((l) => (
                <Menu.Item key={l.id} onClick={() => setLang(l.id)} rightSection={l.id === lang ? <IconCheck size={14} /> : null}>
                  {l.title}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
          <Tooltip label={dark ? t('Светлая тема') : t('Тёмная тема')}>
            <UnstyledButton className={classes.iconBtn} onClick={() => void setTheme(dark ? 'light' : 'dark')} aria-label={t('Тема')}>
              {dark ? <IconSun size={17} /> : <IconMoon size={17} />}
            </UnstyledButton>
          </Tooltip>
        </div>
      </div>
      {tabs && <div className={classes.tabsRow}>{tabs}</div>}

    </div>
  );
}

interface TabsProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  items: { value: T; label: string }[];
}

/** Вкладки режимов экрана: подчёркивание под активной. */
export function ModeTabs<T extends string>({ value, onChange, items }: TabsProps<T>) {
  return (
    <div className={classes.tabs} role="tablist">
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          role="tab"
          aria-selected={item.value === value}
          className={classes.tab}
          data-active={item.value === value || undefined}
          onClick={() => onChange(item.value)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}
